import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { licensePlateRecognitionService } from '../../../services/central-api/license-plate-recognition';
import EventList from '../../../components/lpr/EventList';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { LPRParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CamerasDropdown from '../../../components/lpr/CamerasDropdown';
import { useAuth } from '../../../context/Auth';
import UnitNamesDropdown from '../../../components/lpr/UnitNamesDropdown';
import { WindowInformation } from '../../../services/window-information';
import Container from '../../../components/Container';
import { translate } from '../../../services/translate';
import Header from '../../../components/Header';
import DataTableHeader from '../../../components/DataTableHeader';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';

interface LiveAlertsParams {
    navigation: RootNavigation;
}

export default function LiveAlerts({ navigation }: LiveAlertsParams) {
    const { styles } = useStyles(styleSheet);
    const loaders = useRef({ didSelectedCamerasLoad: false, didSelectedUnitsLoad: false });
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();

    const route = useRoute<RouteProp<LPRParamList, 'LprLiveAlerts'>>();
    const [filters, setFilters] = useFilters<LPRParamList['LprLiveAlerts']>({
        ...route.params,
    });

    const [selectedCameraFilter, setSelectedCameraFilter] = useState<string[]>(filters.cameras ?? []);
    const [selectedUnitNamesIds, setSelectedUnitNamesIds] = useState<number[]>(filters.unitNames ?? []);

    const isFocused = useIsFocused();
    const { data, isLoading } = useQuery({
        queryKey: ['lprLiveAlerts', filters],
        queryFn: () => licensePlateRecognitionService.getIntervalDetections({
            ...filters,
            limit: 50,
            irregularSituation: true,
            isMotorcycle: false,
            page: 0
        }),
        enabled: isFocused,
        refetchInterval: (data) => {
            if (!isFocused) {
                return false;
            }
            if (!data?.refreshInterval) {
                return false;
            }

            return data.refreshInterval;
        },
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!loaders.current.didSelectedCamerasLoad) {
            loaders.current.didSelectedCamerasLoad = true;
            return;
        }
        setFilters({
            ...filters,
            cameras: selectedCameraFilter.length ? selectedCameraFilter : undefined,
        });
        navigation.setParams({
            ...filters,
            cameras: selectedCameraFilter.length ? selectedCameraFilter : undefined,
        });
    }, [selectedCameraFilter]);

    useEffect(() => {
        if (!loaders.current.didSelectedUnitsLoad) {
            loaders.current.didSelectedUnitsLoad = true;
            return;
        }
        setFilters({
            ...filters,
            unitNames: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined
        });
        navigation.setParams({
            ...filters,
            unitNames: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined
        });
    }, [selectedUnitNamesIds]);


    return <Container>
        <Header
            title={translate('liveAlerts')}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredDetections')}
                zIndex={2}
                filter={<View style={styles.header(windowInfo.isMobileOrTablet ? 'column' : 'row')}>
                    <View style={{ flex: 1, zIndex: 3 }}>
                        <CamerasDropdown setValue={setSelectedCameraFilter} value={selectedCameraFilter} zIndex={3} />
                    </View>
                    {(authenticatedUser?.permissions.lpr_watch_unit_detections || authenticatedUser?.permissions.lpr_can_watch_all_events) &&
                        <View style={{ flex: 1, zIndex: 2 }}>
                            <UnitNamesDropdown
                                setValue={setSelectedUnitNamesIds}
                                value={selectedUnitNamesIds}
                                zIndex={2}
                            />
                        </View>
                    }
                </View>}
            />
            <DataTableContainer
                dataTable={<EventList
                    events={data?.responseList ?? []}
                    navigation={navigation}
                    isLoading={isLoading}
                    page='live-alerts'
                />}
            />
        </Card>
    </Container>;
}

const styleSheet = createStyleSheet(() => ({
    header: (direction: 'row' | 'column') => ({
        zIndex: 2,
        flexDirection: direction,
        gap: 10,
        minWidth: direction === 'row' ? 900 : undefined,
    })
}));
